import { groupedImageTimings, imageGroups } from '../constants/pegaMasMultimedia';
import { ImageConfig } from '../interfaces/image.interface';
import { TimeConfig } from '../interfaces/time.interface';

/**
 * Receives result params and returns a config object.
 *
 * @param {number} start - Result display start time in seconds.
 * @param {number} end - Result display end time in seconds.
 * @param {number} firstPos - Left offset (in percentage) for main ball drawing position.
 * @param {number} secondPos - Left offset (in percentage) for top corner results drawing position.
 * @param {number} thirdPos - Left offset (in percentage) for final results drawing position.
 * @returns {PositionConfig}
 */
const getPositionConfigObject = (
  start: number,
  end: number,
  firstPos: number,
  secondPos: number,
  thirdPos?: number
): PositionConfig => ({
  start,
  end,
  firstPos,
  secondPos,
  thirdPos,
});

/**
 * Defines the structure of the position configuration object.
 */
interface PositionConfig {
  start: number;
  end: number;
  firstPos: number;
  secondPos: number;
  thirdPos?: number;
}

/**
 * Returns array of drawing configurations for each individual result.
 *
 * @type {PositionConfig[]}
 */
export const RESULT_POSITION_CONFIG: PositionConfig[] = [
  getPositionConfigObject(26, 27.05, 46.5, 66.4),
  getPositionConfigObject(27.71, 28.73, 46.5, 73.4),
  getPositionConfigObject(29.45, 30.39, 46.5, 80.2),
  getPositionConfigObject(31.15, 32.19, 46.5, 86.8),
  getPositionConfigObject(32.87, 35.15, 43.5, 93.5),
];

/**
 * Calculates the 16:9 proportional height based on a width value.
 *
 * @param {number} width - Width to calculate from.
 * @returns {number} - proportional 16:9 height.
 */
export const getRatioBasedHeight = (width: number): number => {
  const widthRatio = 16;
  const heightRatio = 9;
  const ratio = width / widthRatio;

  return ratio * heightRatio;
};

/**
 * Calculates horizontal drawing position based on width and offset.
 *
 * @param {number} width - Canvas width.
 * @param {number} offsetPct - Left offset in percentage.
 * @returns {number} - X Drawing position;
 */
export const getXDrawingPos = (width: number, offsetPct: number): number =>
  (width * offsetPct) / 100;

/**
 * Calculates vertical drawing position based on width and offset.
 *
 * @param {number} width - Canvas width.
 * @param {number} offsetPct - Top offset in percentage.
 * @returns {number} - Y Drawing position;
 */
export const getYDrawingPos = (width: number, offsetPct: number): number =>
  (getRatioBasedHeight(width) * offsetPct) / 100;

export const RESULTS_SUMMARY_START = 16.85;
export const OVERLAY_TEXT_OPACITY = 0.7;

export const TOP_OFFSETS_CONFIG = {
  main: 59.5,
  top: 9.4,
  summary: 63.55,
};

const generateImageDisplayConfigForAssignment = (groupKey: keyof typeof imageGroups, timeConfig: TimeConfig): ImageConfig[] => {
  const { images } = imageGroups[groupKey];
  const { start, end } = timeConfig;
  const duration = end - start;
  const intervalDuration = duration / images.length;

  return images.map((src: any, index: number) => ({
    src,
    start: start + index * intervalDuration,
    end: start + (index + 1) * intervalDuration,
    x: 0,
    y: 0
  }));
};

export const generateAllImageDisplayConfigs = () => {
  let allConfigs: any[] = [];
  groupedImageTimings.forEach(({ group, timeConfig }) => {
    const groupConfigs = generateImageDisplayConfigForAssignment(group, timeConfig);
    allConfigs = [...allConfigs, ...groupConfigs];
  });
  return allConfigs;
};